import { ReactElement } from 'react';
import { Resources } from 'src/shared/types';

export enum AppViews {
    PROJECTS = 'projects',
    SURVEY = 'survey',
    ADMIN = 'admin',
    MAP = 'map',
    DASHBOARD = 'dashboard',
    ECOLOGY_HUB = 'ecologyHub',
    TABLES = 'tables',
    LOGIN = 'login',
    UPGRADE_SUBSCRIPTION = 'upgradeSubscription',
    HABITAT_INSIGHTS = 'habitatInsights',
    PORTFOLIO = 'portfolio',
    SITE_NATURE_RISK = 'siteNatureRisk',
}

export enum AdminViews {
    GNP = 'gnpManagement',
    /** Every organisation on the platform — deliberately outside the organisation-scoped areas. */
    ORGANISATIONS = 'organisations',
    USER_MANAGEMENT = 'userManagement',
    RISK_PORTFOLIO = 'riskPortfolio',
    ENGINE_API_TOKENS = 'engineApiTokens',
    /** Platform customers that do not agree with the engine organisations. Super admins only. */
    ESTATE_MANAGEMENT = 'estateManagement',
    /** The engine's versioned dataset catalogue. Super admins only. */
    GEOSPATIAL_CATALOGUE = 'geospatialCatalogue',
}

export enum RiskPortfolioViews {
    SITE_MANIFEST_PREVIEW = 'siteManifestPreview',
}

export enum GNPViews {
    ORG_MANAGEMENT = 'gnpOrgManagement',
}

export enum ProjectViews {
    PROJECT_LIST = 'projects',
    NEW_PROJECT_DEFINITION = 'newProjectDefinition',
    EDIT_PROJECT_DEFINITION = 'editProjectDefinition',
    PROJECT_DEFINITION = 'projectDefinition',
    FREEMIUM_SPECIES_INSIGHTS = 'freemiumSpeciesInsights',
    SITE_FILES = 'siteFiles',
    SITE_SUMMARY = 'siteSummary',
}

export enum HabitatInsightViews {
    HABITAT_INSIGHTS_UPLOAD = 'habitatInsightsUpload',
    HABITAT_INSIGHTS_ADD_SITE = 'habitatInsightsAddSite',
}

export enum SurveyViews {
    SURVEY_ADMIN = 'surveyAdmin',
    NEW_SURVEY_DESIGN = 'newSurveyDesign',
    SURVEY_DESIGN_SUMMARY = 'surveyDesignSummary',
}

export enum EcologyHubViews {
    SPECIES_TRENDS = 'speciesTrends',
    STATISTICAL_COMPARISON = 'statisticalComparison',
    TAXONOMIC_COMPOSITION = 'taxonomicComposition',
    COMMUNITY_COMPARISON = 'communityComparison',
    SAMPLE_COMPARISON = 'sampleComparison',
    HABITAT_TRENDS = 'habitatTrends',
}

export enum MapViews {
    SPECIES_INSIGHTS_MAP = 'speciesMap',
    HABITAT_INSIGHTS_MAP = 'habitatMap',
}

export enum TableViews {
    SAMPLE_METRICS = 'sampleMetrics',
    HABITAT_INSIGHTS_METRICS = 'habitatInsightsMetrics',
}

export enum LoginViews {
    FORGOT_PASSWORD = 'forgotPassword',
    VERIFY_CUSTOM_CHALLENGE = 'verifyCustomChallenge',
    VERIFY_PHONE_NUMBER = 'verifyPhoneNumber',
    SET_NEW_PASSWORD = 'setNewPassword',
    TERMS_OF_USE = 'termsOfUse',
    TERMS_OF_USE_DECLINED = 'termsOfUseDeclined',
    SIGNIN = 'signin',
    IMPERSONATE_USER = 'impersonateUser',
    POST_AUTH_REDIRECT = 'postAuthRedirect',
}

export enum PortfolioViews {
    PORTFOLIO_SUMMARY = 'portfolioSummary',
    PORTFOLIO_SITE_EXPLORER = 'portfolioSiteExplorer',
    SPECIES_RISK_INDICATORS = 'speciesRiskIndicators',
    GLOBAL_NATURE_RISK_SITE_LIST = 'globalNatureRiskSiteList',
    GLOBAL_NATURE_RISK_OVERVIEW = 'globalNatureRiskOverview',
    GLOBAL_NATURE_RISK_STATE_OF_NATURE = 'globalNatureRiskStateOfNature',
    GLOBAL_NATURE_RISK_IMPACTS_AND_DEPENDENCIES = 'globalNatureRiskImpactsAndDependencies',
}

export enum SiteNatureRiskViews {
    OVERVIEW = 'overview',
    CHARACTERISTICS = 'characteristics',
    STATE_OF_NATURE = 'stateOfNature',
    IMPACTS_AND_DEPENDENCIES = 'impactsAndDependencies',
}

export type IndividualRouteSetting = {
    name: string;
    path: string;
    handler: ReactElement;
    resource: Resources;
};

export type AppRoutesAvailability = { [key in AppViews]: boolean };
export type InsightRoutesAvailability = { [key in EcologyHubViews]: boolean };
export type MapRoutesAvailability = { [key in MapViews]: boolean };
export type AdminRoutesAvailability = { [key in AdminViews]: boolean };
export type ProjectRoutesAvailability = { [key in ProjectViews]: boolean };
export type SurveyRoutesAvailability = { [key in SurveyViews]: boolean };
export type TableRoutesAvailability = { [key in TableViews]: boolean };
export type HabitatInsightRoutesAvailability = { [key in HabitatInsightViews]: boolean };
export type PortfolioRoutesAvailability = { [key in PortfolioViews]: boolean };
export type SiteNatureRiskRoutesAvailability = { [key in SiteNatureRiskViews]: boolean };

export const appRoutesAvailability: AppRoutesAvailability = {
    projects: false,
    survey: false,
    admin: false,
    map: false,
    dashboard: false,
    ecologyHub: false,
    tables: false,
    login: true,
    upgradeSubscription: true,
    habitatInsights: false,
    portfolio: false,
    siteNatureRisk: false,
};

export const adminRoutesAvailability: AdminRoutesAvailability = {
    gnpManagement: false,
    organisations: false,
    userManagement: false,
    riskPortfolio: false,
    engineApiTokens: false,
    estateManagement: false,
    geospatialCatalogue: false,
};

export const mapRoutesAvailability: MapRoutesAvailability = {
    speciesMap: false,
    habitatMap: false,
};

export const projectRoutesAvailability: ProjectRoutesAvailability = {
    projects: true,
    newProjectDefinition: false,
    editProjectDefinition: false,
    projectDefinition: false,
    freemiumSpeciesInsights: false,
    siteFiles: true,
    siteSummary: true,
};

export const surveyRoutesAvailability: SurveyRoutesAvailability = {
    surveyAdmin: false,
    newSurveyDesign: false,
    surveyDesignSummary: false,
};

export const habitatInsightsRoutesAvialability: HabitatInsightRoutesAvailability = {
    habitatInsightsUpload: false,
    habitatInsightsAddSite: false,
};

export const insightRoutesAvailability: InsightRoutesAvailability = {
    speciesTrends: true,
    habitatTrends: false,
    statisticalComparison: true,
    taxonomicComposition: true,
    communityComparison: true,
    sampleComparison: true,
};

export const tableRoutesAvailability: TableRoutesAvailability = {
    sampleMetrics: true,
    habitatInsightsMetrics: true,
};

export const portfolioRoutesAvailability: PortfolioRoutesAvailability = {
    portfolioSummary: true,
    speciesRiskIndicators: true,
    portfolioSiteExplorer: true,
    globalNatureRiskSiteList: true,
    globalNatureRiskOverview: true,
    globalNatureRiskStateOfNature: true,
    globalNatureRiskImpactsAndDependencies: true,
};

export const siteNatureRiskRoutesAvailability: SiteNatureRiskRoutesAvailability = {
    overview: true,
    characteristics: true,
    stateOfNature: true,
    impactsAndDependencies: true,
};
